import React, { useEffect, useState } from 'react';
import { Edit, Plus, RefreshCw, Search, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';

import DataTable from '../components/DataTable';
import LoadingSkeleton from '../components/LoadingSkeleton';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { teamsApi } from '../lib/api';

export default function Teams() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [activeTeam, setActiveTeam] = useState(null);
  const [form, setForm] = useState({ name: '' });
  const [saving, setSaving] = useState(false);

  const loadTeams = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await teamsApi.list();
      setTeams(res.data?.items || []);
    } catch (err) {
      toast.error('Failed to load departments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTeams(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await teamsApi.create(form);
      toast.success('Department created');
      setShowAdd(false);
      setForm({ name: '' });
      loadTeams({ silent: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create department');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await teamsApi.update(activeTeam.id, form);
      toast.success('Department updated');
      setShowEdit(false);
      setActiveTeam(null);
      setForm({ name: '' });
      loadTeams({ silent: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update department');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this department?')) return;
    try {
      await teamsApi.delete(id);
      toast.success('Department deleted');
      loadTeams({ silent: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete department');
    }
  };

  const filteredTeams = teams.filter(t => 
    t.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen space-y-6 bg-slate-900 p-6 text-slate-300">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Departments</h1>
          <p className="mt-2 text-slate-500">
            Manage organizational units and team assignments.
          </p>
        </div>
        <Button className="bg-blue-600 text-white hover:bg-blue-700" onClick={() => setShowAdd(true)}>
          <Plus className="h-4 w-4" />
          Add Department
        </Button>
      </div>

      <Card className="border-slate-700 bg-slate-800 shadow-lg">
        <CardHeader className="pb-0 flex flex-row items-center justify-between">
          <CardTitle className="text-white">Department List</CardTitle>
          <Button variant="ghost" size="icon" className="text-slate-500 hover:text-white" onClick={() => loadTeams()}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search departments..."
              className="w-full rounded-lg border border-slate-600 bg-slate-700 py-3 pl-10 pr-4 text-white placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {loading ? (
            <LoadingSkeleton type="table" />
          ) : (
            <DataTable
              columns={[
                { key: 'name', label: 'Department Name' },
                { 
                  key: 'employee_count', 
                  label: 'Employees', 
                  render: (val) => (
                    <div className="flex items-center gap-2">
                      <Users className="h-3 w-3 text-slate-500" />
                      {val || 0}
                    </div>
                  )
                },
                {
                  key: 'actions',
                  label: 'Actions',
                  render: (_, row) => (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-slate-600 bg-slate-900 text-slate-300 hover:bg-slate-700"
                        onClick={() => {
                          setActiveTeam(row);
                          setForm({ name: row.name });
                          setShowEdit(true);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                        Rename
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-500/20 bg-red-500/10 text-red-400 hover:bg-red-500/20"
                        onClick={() => handleDelete(row.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </Button>
                    </div>
                  ),
                },
              ]}
              data={filteredTeams}
            />
          )}
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="border-slate-700 bg-slate-800 text-slate-300">
          <DialogHeader>
            <DialogTitle className="text-white">Add New Department</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 pt-4">
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase text-slate-500">Department Name</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ name: e.target.value })}
                placeholder="e.g. Engineering, Sales, Human Resources"
                className="w-full rounded-lg border border-slate-700 bg-slate-900 px-4 py-3 text-white outline-none focus:ring-2 focus:ring-blue-500"
                required
                autoFocus
              />
            </div>
            <Button type="submit" className="w-full bg-blue-600 text-white hover:bg-blue-700" disabled={saving}>
              {saving ? 'Creating...' : 'Create Department'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="border-slate-700 bg-slate-800 text-slate-300">
          <DialogHeader>
            <DialogTitle className="text-white">Rename Department</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4 pt-4">
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase text-slate-500">Department Name</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ name: e.target.value })}
                className="w-full rounded-lg border border-slate-700 bg-slate-900 px-4 py-3 text-white outline-none focus:ring-2 focus:ring-blue-500"
                required
                autoFocus
              />
            </div>
            <Button type="submit" className="w-full bg-blue-600 text-white hover:bg-blue-700" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
